import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { SEO } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CONTACT } from '../../constants/images';

export const WomensHealth = () => {
  const services = [
    {
      title: 'Uterine Fibroids — Uterine Artery Embolization (UAE)',
      description: 'UAE is a minimally invasive procedure in which tiny embolic particles are injected through a catheter into the uterine arteries — cutting off blood supply to fibroids. Deprived of blood, fibroids shrink over 3–6 months while the uterus is fully preserved.',
      symptoms: ['Heavy or prolonged menstrual bleeding with clots', 'Pelvic pressure or pain', 'Frequent urination or difficulty emptying bladder', 'Lower back pain', 'Anaemia from repeated heavy blood loss', 'Difficulty conceiving in select cases'],
      treatments: ['UAE — Uterus-preserving alternative to hysterectomy', '60–90 minute procedure under fluoroscopy', '1 night hospital stay, 1-2 weeks to full activity', 'Fibroid shrinkage of 50-70% within 6 months', 'Patient satisfaction comparable to hysterectomy']
    },
    {
      title: 'Pelvic Congestion Syndrome — Ovarian Vein Embolization',
      description: 'Pelvic Congestion Syndrome (PCS) is a cause of chronic pelvic pain in women of reproductive age — caused by dilated pelvic veins, essentially varicose veins of the ovaries and pelvic tissues.',
      symptoms: ['Dull aching pelvic pain for more than 6 months', 'Pain worsened by prolonged standing', 'Pain in second half of menstrual cycle or after intercourse', 'Pelvic heaviness or pressure', 'Varicose veins on buttocks, inner thighs, or vulva', 'Abnormal or heavy uterine bleeding'],
      treatments: ['Ovarian Vein Embolization — embolic coils block dilated veins', 'Light sedation, no surgical incision', 'Day-care procedure', 'Performed at Mumbai locations']
    }
  ];

  const faqs = [
    { q: 'Can fibroids be treated without hysterectomy in Mumbai?', a: 'Many women can avoid hysterectomy through uterus-preserving options like UAE. Dr. Punit Mahajan performs UAE in Mumbai — it preserves the uterus and has excellent outcomes for controlling heavy bleeding.' },
    { q: 'Does UAE affect fertility?', a: 'UAE is generally not the first-line treatment for women wishing to conceive soon. Women with fertility goals should discuss all options — including myomectomy — with both a gynaecologist and Dr. Punit Mahajan.' },
    { q: 'How quickly do fibroids shrink after UAE?', a: 'Most women notice improvement in heavy bleeding from the first menstrual cycle after UAE. Significant fibroid volume reduction of 50-70% typically occurs within 3-6 months.' },
    { q: 'What is recovery like after UAE?', a: 'Most women return to light activity within 1 week and full activity within 2 weeks. Post-embolization syndrome (mild fever and cramping) is common for 3-5 days.' },
    { q: 'Is Pelvic Congestion Syndrome treatable in Mumbai?', a: 'Yes. Ovarian vein embolization is a minimally invasive day-care procedure that treats PCS without surgery.' }
  ];

  return (
    <>
      <SEO 
        title="Uterine Fibroid Treatment Without Surgery in Mumbai | Dr. Punit Mahajan"
        description="Uterine Artery Embolization (UAE) for fibroids & ovarian vein embolization for pelvic congestion syndrome in Mumbai. Uterus-preserving, minimally invasive care by Dr. Punit Mahajan."
      />

      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden"><div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">Women's Health Interventions</h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>Uterus-Sparing, Non-Surgical Solutions in Mumbai</p>
        </div>
      </section>

      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose max-w-none mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Introduction</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              For decades, hysterectomy was the default treatment for symptomatic uterine fibroids. Today, Interventional Radiology offers a proven, catheter-based alternative that treats fibroids without removing the uterus, without general anaesthesia in most cases, and with recovery measured in days. Dr. Punit Mahajan offers UAE and ovarian vein embolization in Mumbai.
            </p>
          </div>

          <div className="space-y-8">
            {services.map((service, index) => (
              <Card key={index} className="card-gold-hover transition-all bg-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">{service.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Symptoms:</h4>
                      <ul className="space-y-2">
                        {service.symptoms.map((s, idx) => (
                          <li key={idx} className="flex items-start"><CheckCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{s}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Treatment Details:</h4>
                      <ul className="space-y-2">
                        {service.treatments.map((t, idx) => (
                          <li key={idx} className="flex items-start"><ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{t}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="border-l-4 border-amber-500 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">Preserve Your Uterus — Avoid Hysterectomy</h2>
          <p className="text-xl text-slate-300">Discover the minimally invasive alternative to surgery</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${CONTACT.phone}`}><Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">Call {CONTACT.phone}</Button></a>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">Book Consultation</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
};
